import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  CLIENT_APP_ID,
  ClientAppId,
  Dossier,
  DossierCommentsEnum,
  Fitter,
  getGlassNameKey,
  getLatestDossierCommentTextByType,
  getOperationWithGlassKey,
  getInsuredMessage,
  getCoverMessage,
  getCoverRangeMessage,
  MessageInfos,
} from '@sc/data-access-models';
import { LocaleDatePipe, TranslatorService } from '@sc/data-access-translation';
import { FitterRouterService } from '@sc/feature-fitter-path-router-system';
import { SnackbarService } from '@sc/feature-snackbar';
import {
  IconComponent,
  InsurerLogoComponent,
  MomentParseZonePipe,
  SpinnerComponent,
} from '@sc/ui-components';
import { dayjs } from '@sc/utils';
import { map, switchMap, take } from 'rxjs/operators';
import {
  PlanningCalendarService,
  PlanningSandboxService,
} from '../../../services';
import { FitterNameDisplayService } from '@sc/feature-contextual-display';

@Component({
  selector: 'sc-mobile-planning-dossier',
  styleUrls: ['./mobile-planning-dossier.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    IconComponent,
    InsurerLogoComponent,
    NgIf,
    NgClass,
    NgFor,
    SpinnerComponent,
    AsyncPipe,
    MomentParseZonePipe,
    TranslateModule,
    LocaleDatePipe,
  ],
  providers: [PlanningSandboxService, FitterNameDisplayService],
  template: `
    <div class="custom-mobile-navbar">
      <span class="return-arrow" tabindex="0" (click)="goBack()"
        ><sc-icon name="arrow_left"></sc-icon
      ></span>
      <span class="insurer-logo-container"
        ><sc-insurer-logo></sc-insurer-logo
      ></span>
    </div>

    <section *ngIf="isDossierLoaded()" class="mobile-dossier-page-container">
      <h2>
        {{
          (dossier.claim?.car?.licensePlateNumber
            ? 'PlanningMobileDossier.titleWithPlate'
            : 'PlanningMobileDossier.title'
          )
            | translate
              : { licensePlate: dossier.claim?.car?.licensePlateNumber }
        }}
      </h2>

      <article
        class="appointment-infos-card dossier-infos-section"
        [class.outside-appointment]="dossier.appointment?.outsideIntervention"
      >
        <div class="appointment-card-title">
          <h2>{{ 'PlanningMobileDossier.appointmentTitle' | translate }}</h2>
          <sc-icon
            *ngIf="dossier?.creationData?.origin !== 'AGENDA'"
            [name]="getOriginIcon()"
          ></sc-icon>
        </div>
        <h3>{{ 'PlanningMobileDossier.appointmentInfosTitle' | translate }}</h3>
        <div class="apmt-info-box">
          {{
            dossier.appointment?.time
              | parseZoneTimeString: dossier.appointment?.timezone
              | localeDate: 'fullDate'
              | async
          }}
        </div>
        <div class="apmt-info-box">
          {{
            dossier.appointment?.time
              | parseZoneTimeString: dossier.appointment?.timezone
              | localeDate: 'shortTime'
              | async
          }}
          -
          {{
            dossier.appointment?.endTime
              | parseZoneTimeString: dossier.appointment?.timezone
              | localeDate: 'shortTime'
              | async
          }}
        </div>
        <div class="apmt-info-box">
          <sc-icon
            [name]="
              dossier.appointment?.outsideIntervention ? 'home' : 'fitter'
            "
          ></sc-icon>
          <span>{{ getFitterLocationTxtKey() | translate }}</span>
        </div>
        <div class="apmt-info-box">
          <sc-icon name="car"></sc-icon>
          <span>
            {{
              'PlanningMobileDossier.replacementVehicleIndication'
                | translate
                  : { rv: !!dossier.appointment?.isReplacementVehicleSelected }
            }}
          </span>
        </div>
        <div
          class="apmt-info-box"
          *ngIf="dossier.appointment?.outsideIntervention"
        >
          <span>{{ dossier.appointment?.interventionLocation?.address }}</span>
        </div>
        <div *ngIf="fitter" class="apmt-info-box display-rows">
          <span>{{ fitter?.brand }}</span>
          <span>{{ getFitterFullAddress() }}</span>
        </div>
        <div *ngIf="getTechnicianList()" class="apmt-info-box">
          {{ getTechnicianList() }}
        </div>
        <div
          *ngIf="!!dossier?.appointment?.assignedInterventionVehicle"
          class="apmt-info-box"
        >
          {{ dossier.appointment.assignedInterventionVehicle }}
        </div>
        <div *ngIf="getComment()" class="comment-container">
          <h3>{{ 'PlanningMobileDossier.commentTitle' | translate }}</h3>
          <p>
            {{ getComment() }}
          </p>
        </div>
      </article>
      <article class="dossier-infos-section">
        <h2>{{ 'PlanningMobileDossier.customerInfosTitle' | translate }}</h2>
        <div class="info-box">
          <div class="info-box-line">
            <sc-icon name="user"></sc-icon>
            <span
              >{{ dossier.customer?.firstname || '' }}
              {{ dossier.customer?.lastname || '' }}</span
            >
          </div>
          <div class="info-box-line">
            <sc-icon name="phone"></sc-icon>
            <span>{{ dossier.customer?.phoneNumber?.number || '-' }}</span>
          </div>
          <div class="info-box-line">
            <sc-icon name="mail"></sc-icon>
            <span>{{ dossier.customer?.email || '-' }}</span>
          </div>
        </div>
      </article>
      <article class="dossier-infos-section">
        <h2>{{ 'PlanningMobileDossier.interventionTitle' | translate }}</h2>
        <div class="info-box">
          <div class="info-box-line">
            <sc-icon name="plaque"></sc-icon>
            <span>{{ dossier.claim?.car?.licensePlateNumber || '-' }}</span>
          </div>
          <div class="info-box-line">
            <sc-icon name="car"></sc-icon>
            <span>{{ formatCarBrandModel() }}</span>
          </div>
          <div class="info-box-line">
            <sc-icon name="car"></sc-icon>
            <span>{{ formatVehicleTypeTextKey() | translate }}</span>
          </div>
          <div class="info-box-line">
            <sc-icon name="accident"></sc-icon>
            <span>{{ getOperationName() }}</span>
          </div>
          <div
            *ngIf="dossier.claim?.calibration === 'true'"
            class="info-box-line"
          >
            <sc-icon name="adas"></sc-icon>
            <span>{{
              'PlanningMobileDossier.calibrationIndication' | translate
            }}</span>
          </div>
        </div>
        <div class="info-box">
          <div *ngIf="dossier.claim?.eurocode" class="info-box-line">
            <span>{{
              'PlanningMobileDossier.eurocodeIndication' | translate
            }}</span
            >&nbsp;
            <span
              ><strong>{{ dossier?.claim?.eurocode }}</strong></span
            >
          </div>
          <div class="info-box-line">
            <span>{{
              'PlanningMobileDossier.partAvailabilityIndication' | translate
            }}</span>
            &nbsp;
            <span style="display: flex; align-items: center;">
              <sc-icon
                name="operation_vitre"
                [ngClass]="{
                  unavailable: getPartAvailability() === 'UNAVAILABLE',
                  ordered: getPartAvailability() === 'ORDERED',
                  available: getPartAvailability() === 'AVAILABLE'
                }"
              ></sc-icon>
              <strong>{{
                'GlobalApplicativeConstants.partAvailabilities.' +
                  getPartAvailability() | translate
              }}</strong>
            </span>
          </div>
          <div class="info-box-line">
            <span>{{
              'PlanningMobileDossier.partProviderIndication' | translate
            }}</span
            >&nbsp;
            <span
              ><strong>{{
                dossier.appointment?.partProvider || '-'
              }}</strong></span
            >
          </div>
        </div>
      </article>
      <article class="dossier-infos-section">
        <h2>{{ 'PlanningMobileDossier.mobilityTitle' | translate }}</h2>
        <div class="info-box">
          <div class="info-box-line">
            <sc-icon
              [name]="
                dossier.appointment?.outsideIntervention ? 'home' : 'fitter'
              "
            ></sc-icon>
            <span *ngIf="!dossier.appointment?.outsideIntervention">{{
              getFitterLocationTxtKey() | translate
            }}</span>
            <span *ngIf="dossier.appointment?.outsideIntervention">{{
              dossier.appointment?.interventionLocation?.address
            }}</span>
          </div>
          <div class="info-box-line">
            <sc-icon name="car"></sc-icon>
            <span>
              {{
                'PlanningMobileDossier.replacementVehicleIndication'
                  | translate
                    : {
                        rv: !!dossier.appointment?.isReplacementVehicleSelected
                      }
              }}
            </span>
          </div>
          <div
            *ngIf="dossier.appointment.assignedReplacementVehicle"
            class="info-box-line"
          >
            <sc-icon name="car"></sc-icon>
            <span>{{
              dossier.appointment?.assignedReplacementVehicle.name
            }}</span>
            <span
              *ngIf="
                dossier.appointment?.assignedReplacementVehicle
                  ?.licensePlateNumber
              "
              class="light-text"
            >
              &nbsp;-
              {{
                dossier.appointment.assignedReplacementVehicle
                  .licensePlateNumber
              }}</span
            >
          </div>
        </div>
      </article>
      <article class="dossier-infos-section">
        <h2>{{ 'PlanningMobileDossier.insuranceTitle' | translate }}</h2>
        <div class="info-box">
          <div
            *ngIf="dossier.claim?.car?.ownerShip?.type"
            class="info-box-line"
          >
            <span>{{
              'PlanningMobileDossier.ownershipIndication' | translate
            }}</span>
            &nbsp;<span
              ><strong>{{
                'GlobalApplicativeConstants.ownerShipType.' +
                  dossier.claim?.car?.ownerShip?.type | translate
              }}</strong></span
            >
          </div>
          <div
            *ngIf="dossier.policyInformation?.insuranceName"
            class="info-box-line"
          >
            <span>{{
              'PlanningMobileDossier.insuranceIndication' | translate
            }}</span>
            &nbsp;<span
              ><strong>{{
                dossier.policyInformation?.insuranceName
              }}</strong></span
            >
          </div>
          <div
            *ngIf="dossier.policyInformation?.policyNumber"
            class="info-box-line"
          >
            <span>{{
              'PlanningMobileDossier.policyNumberIndication' | translate
            }}</span>
            &nbsp;<span
              ><strong>{{
                dossier.policyInformation?.policyNumber
              }}</strong></span
            >
          </div>
          <div *ngIf="!!insuranceMessage.key" class="insurance-infos-container">
            <p
              class="insurance-text sc-design-text-3"
              [innerHTML]="
                insuranceMessage.key | translate: insuranceMessage.params
              "
            ></p>
            <p
              *ngIf="!!coverMessage.key"
              class="cover-text sc-design-text-3"
              [innerHTML]="coverMessage.key | translate: coverMessage.params"
            ></p>
            <p
              *ngIf="!!coverRangeMessage.key"
              class="cover-range-text sc-design-text-3"
              [innerHTML]="
                coverRangeMessage.key | translate: coverRangeMessage.params
              "
            ></p>
          </div>
          <div class="info-box-line">
            <sc-icon name="calendar_hour"></sc-icon>
            <span>{{ getSinisterDate() }}</span>
          </div>
          <div class="info-box-line">
            <sc-icon name="map_location"></sc-icon>
            <span>{{ dossier.claim?.sinisterLocation || '-' }}</span>
          </div>
          <div class="info-box-line">
            <sc-icon name="accident"></sc-icon>
            <span>{{
              (dossier.claim?.sinisterContext?.value | translate) || '-'
            }}</span>
          </div>
        </div>
      </article>
      <article class="dossier-infos-section">
        <h2>{{ 'PlanningMobileDossier.documentsTitle' | translate }}</h2>
        <div
          *ngFor="let document of dossier.claim?.documents || []"
          class="document-box"
        >
          <sc-icon name="picture_preview"></sc-icon>
          <a [href]="document.url">{{ document.name }}</a>
        </div>
        <div *ngIf="!dossier.claim?.documents?.length">
          <span>{{ 'PlanningMobileDossier.noDocumentsHint' | translate }}</span>
        </div>
      </article>
    </section>
    <section
      *ngIf="!isDossierLoaded()"
      class="mobile-dossier-page-container loading-page"
    >
      <span>{{ 'PlanningMobileDossier.loadingDossierHint' | translate }}</span>
      <sc-spinner></sc-spinner>
    </section>
  `,
})
export class MobilePlanningDossierComponent implements OnInit, OnDestroy {
  dossierId: string;

  dossier: Dossier;
  fitter: Fitter;

  insuranceMessage: MessageInfos;
  coverMessage: MessageInfos;
  coverRangeMessage: MessageInfos;

  constructor(
    private readonly router: FitterRouterService,
    private readonly route: ActivatedRoute,
    private readonly planningSandbox: PlanningSandboxService,
    private readonly planningCalendarService: PlanningCalendarService,
    private readonly translator: TranslatorService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly snackbarService: SnackbarService,
    private readonly configurationService: ConfigurationService,
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId
  ) {}

  ngOnInit() {
    this.snackbarService.setFocusManagement(true, {
      documentQuery: 'sc-mobile-planning-dossier',
    });

    this.route.params.subscribe((params) => {
      this.dossierId = params?.dossierId;

      this.planningSandbox
        .getDossier(this.dossierId)
        .pipe(
          switchMap((resultDossier) =>
            this.planningSandbox.fitters$.pipe(
              take(1),
              map((fitters) => {
                this.fitter = fitters.find(
                  (f) => f.id === resultDossier.appointment?.fitterId
                );
                this.changeDetector.detectChanges();
                return resultDossier;
              })
            )
          )
        )
        .subscribe((resultDossier) => {
          // If dossier is not complete, then redirect to planning
          if (!resultDossier.appointment?.id) {
            this.snackbarService.showError(
              'Snackbar.errorTitle',
              'PlanningMobileDossier.unreadableDossierMessage'
            );
            this.router.goToPlanning();
          } else {
            this.dossier = resultDossier;
            this.insuranceMessage = getInsuredMessage(
              this.dossier,
              { coverAllGlasses: false, defaultIsInsuredWithoutGlass: false },
              this.clientAppId
            );
            this.coverMessage = getCoverMessage(
              this.dossier,
              { coverAllGlasses: false, defaultIsInsuredWithoutGlass: false },
              this.clientAppId
            );
            this.coverRangeMessage = getCoverRangeMessage(
              this.dossier,
              { coverAllGlasses: false, defaultIsInsuredWithoutGlass: false },
              this.clientAppId
            );
            this.changeDetector.detectChanges();
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.snackbarService.setFocusManagement(false);
  }

  goBack() {
    this.route.queryParams.subscribe((queryParams) => {
      if (queryParams.previousUrl) {
        this.router.goToPreviousPage(queryParams.previousUrl);
      } else if (this.fitter?.id) {
        this.router.gotToMobileCalendar(this.fitter.id);
      } else {
        this.router.goToPlanning();
      }
    });
  }

  isDossierLoaded() {
    return !!this.dossier?.id;
  }

  getOriginIcon() {
    return this.planningCalendarService.getOriginIcon(
      this.configurationService.getDossierGlobalOriginFromCreationData(
        this.dossier.creationData,
        this.clientAppId
      )
    );
  }

  getComment() {
    return (
      getLatestDossierCommentTextByType(this.dossier, [
        DossierCommentsEnum.appointment,
        DossierCommentsEnum.sinister,
        DossierCommentsEnum.insurer,
      ]) ||
      this.dossier.fitterInfo?.message ||
      ''
    );
  }

  getFitterLocationTxtKey() {
    if (this.dossier.appointment?.outsideIntervention) {
      return 'PlanningFormCenter.atHome';
    } else {
      return 'PlanningFormCenter.inCenter';
    }
  }

  getTechnicianList() {
    return this.dossier?.appointment?.assignedResources?.join(', ');
  }

  getFitterFullAddress() {
    if (!this.fitter) {
      return '';
    }

    return `${this.fitter.address}, ${this.fitter.cp} ${this.fitter.city}`;
  }

  getSinisterDate(): string {
    const date = dayjs(this.dossier?.claim?.sinisterDate);
    return date.isValid() ? date.tz(this.fitter?.timezone).format('LLLL') : '-';
  }

  formatVehicleTypeTextKey() {
    if (this.dossier.claim?.car?.vehicleType) {
      return `PlanningFormAppointmentIntervention.vehicleTypeNames.${this.dossier.claim.car.vehicleType}`;
    }

    return 'FitterDossierSearchPage.Results.vehicleTypeNotFound';
  }

  formatCarBrandModel() {
    return (
      [
        this.dossier.claim?.car?.manufacturer,
        this.dossier.claim?.car?.model,
      ].join(' - ') ||
      this.translator.getInstantTranslation(
        'PlanningMobileDossier.unknownVehicle'
      )
    );
  }

  getPartAvailability() {
    return this.dossier.appointment?.partAvailability || 'UNAVAILABLE';
  }

  getOperationName() {
    const foundOperation = this.fitter?.serviceTypes?.find(
      (op) => op.id === this.dossier.appointment?.interventionId
    );

    if (foundOperation) {
      return foundOperation.name;
    }

    return this.translator.getInstantTranslation(
      getOperationWithGlassKey(this.dossier.claim?.operationType),
      {
        glassName: this.translator.getInstantTranslation(
          getGlassNameKey(this.dossier.claim?.glass)
        ),
      }
    );
  }
}
